import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, DollarSign, Users, Building2, Target, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";

function Metric({ icon: Icon, label, value, sub, accent = false }) {
  return (
    <div className={`border rounded-xl p-5 ${accent ? "bg-navy text-white border-navy" : "bg-white border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-xs uppercase tracking-widest ${accent ? "text-gold" : "text-slate-500"}`}>{label}</div>
          <div className={`font-display text-3xl font-bold mt-2 ${accent ? "text-white" : "text-navy"}`}>{value}</div>
          {sub && <div className={`text-xs mt-1 ${accent ? "text-slate-200" : "text-slate-500"}`}>{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${accent ? "bg-gold/20 text-gold" : "bg-gold/10 text-gold"}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function Progress({ label, current, target, fmt = (v) => v }) {
  const pct = target ? Math.min(100, Math.round((current / target) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <div className="text-slate-600">{label}</div>
        <div className="text-navy font-medium">{fmt(current)} / {fmt(target)}</div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function GrowthDashboard() {
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState({});
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/success/growth");
    setData(data);
    setGoals(data.goals);
  };
  useEffect(() => { load(); }, []);

  const saveGoals = async (e) => {
    e.preventDefault();
    await api.put("/success/growth/goals", {
      ...goals,
      daily_outreach: parseInt(goals.daily_outreach) || 0,
      weekly_screens: parseInt(goals.weekly_screens) || 0,
      monthly_placements: parseInt(goals.monthly_placements) || 0,
      monthly_revenue: parseFloat(goals.monthly_revenue) || 0,
      commission_percent: parseFloat(goals.commission_percent) || 0,
    });
    toast.success("Goals updated");
    setEditing(false);
    load();
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const k = data.kpi;

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-2">Success Center</div>
        <h1 className="font-display text-3xl font-bold text-navy">Business Growth Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">The health of your recruiting business, at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric icon={Target} label="Placements this month" value={k.placements_this_month} sub={`Goal: ${goals.monthly_placements}`} accent />
        <Metric icon={DollarSign} label="Revenue this month" value={`$${k.revenue_this_month.toLocaleString()}`} sub={`Goal: $${(goals.monthly_revenue || 0).toLocaleString()}`} />
        <Metric icon={TrendingUp} label="Commission projection" value={`$${k.commission_projection.toLocaleString()}`} sub={`${goals.commission_percent}% take`} />
        <Metric icon={Clock} label="Avg time to fill" value={`${k.avg_time_to_fill_days}d`} />

        <Metric icon={Users} label="Candidate growth (30d)" value={`${k.candidate_growth_pct}%`} />
        <Metric icon={Building2} label="Employer growth (30d)" value={`${k.employer_growth_pct}%`} />
        <Metric icon={Target} label="Fill rate" value={`${k.fill_rate_pct}%`} sub={`${k.jobs_filled} filled`} />
        <Metric icon={MessageCircle} label="Response rates" value={`C ${k.candidate_response_rate}% • E ${k.employer_response_rate}%`} />
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="md:col-span-2 border border-slate-200 rounded-xl bg-white p-6">
          <div className="eyebrow mb-4">Monthly progress</div>
          <div className="space-y-4">
            <Progress label="Placements" current={data.progress.monthly_placements.current} target={data.progress.monthly_placements.target} />
            <Progress label="Revenue" current={data.progress.monthly_revenue.current} target={data.progress.monthly_revenue.target} fmt={v => `$${v.toLocaleString()}`} />
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl bg-white p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="eyebrow">Goals</div>
            <button onClick={() => setEditing(!editing)} className="text-xs text-navy hover:text-gold">{editing ? "Cancel" : "Edit"}</button>
          </div>
          {editing ? (
            <form onSubmit={saveGoals} className="space-y-3 text-sm">
              {[
                ["daily_outreach", "Daily outreach"],
                ["weekly_screens", "Weekly screens"],
                ["monthly_placements", "Monthly placements"],
                ["monthly_revenue", "Monthly revenue ($)"],
                ["commission_percent", "Commission %"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-slate-500">{label}</label>
                  <input type="number" value={goals[k] ?? ""} onChange={e => setGoals({...goals, [k]: e.target.value})}
                    className="w-full border rounded px-3 py-2 mt-1" />
                </div>
              ))}
              <button type="submit" className="btn-navy w-full justify-center">Save goals</button>
            </form>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Daily outreach</span><span className="font-medium text-navy">{goals.daily_outreach}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Weekly screens</span><span className="font-medium text-navy">{goals.weekly_screens}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Monthly placements</span><span className="font-medium text-navy">{goals.monthly_placements}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Monthly revenue</span><span className="font-medium text-navy">${(goals.monthly_revenue || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Commission %</span><span className="font-medium text-navy">{goals.commission_percent}%</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
